import React, {Component} from 'react';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Alert from '@material-ui/lab/Alert';
import Button from '../../../components/button';

/**
 * Mockup screen S1 — "People & access" (docs/mockups/access-control-mockups.html; slice 4b-2 §3.4,
 * §3.5). The admin home.
 *
 * REPLACES A SEARCH BOX. 4b shipped a picker-first page: an operator had to already know whose name
 * to type before it would show them anything, and the picker rendered "No options" until a
 * keystroke — which reads as "there are no subjects".
 *
 * The column the mockup singles out as "the column that ends support tickets" is **Source**: it
 * says whether access came from a role, from owning something, from a group, from the identity
 * provider, or from a delegation.
 */

const SOURCES = [
  {value: '', label: 'All sources'},
  {value: 'direct', label: 'Direct'},
  {value: 'ownership', label: 'Ownership'},
  {value: 'delegation', label: 'Delegation'},
  {value: 'idp', label: 'IdP claim'},
  {value: 'backfill', label: 'Backfill'},
];

const SCOPE_TYPES = [
  {value: '', label: 'All scopes'},
  {value: 'global', label: 'global'},
  {value: 'instance', label: 'instance'},
  {value: 'schema', label: 'schema'},
  {value: 'object', label: 'object'},
  {value: 'connection', label: 'connection'},
  {value: 'context', label: 'context'},
];

const scopeText = (s) => (s.type === 'global' ? 'global' : `${s.type} · ${s.label}`);
const expiryText = (v) => (v ? new Date(v).toLocaleDateString() : '—');

/** "84 people, 7 teams, 2 service accounts" — per-type, as drawn, not one total. */
const countsText = (counts) => {
  const parts = [];
  const say = (n, one, many) => (n ? parts.push(`${n} ${n === 1 ? one : many}`) : null);
  say(counts.user, 'person', 'people');
  say(counts.group, 'team', 'teams');
  say(counts.service, 'service account', 'service accounts');
  return parts.join(', ');
};

class RosterTable extends Component {
  /** CSV of what is on screen, honouring the page cap — labelled with it so it cannot mislead. */
  handleExport = () => {
    const rows = [['Subject', 'Type', 'Role or permission', 'Scope', 'Source', 'Expires', 'Via group']];
    (this.props.people || []).forEach((p) => {
      (p.lines || []).forEach((l) => rows.push([
        p.displayName, p.subjectType, l.roleCode || l.permissionCode || '',
        scopeText(l.scope), l.source.label, expiryText(l.expiresAt), l.derived ? 'yes' : 'no',
      ]));
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], {type: 'text/csv'}));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'access-roster.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  render() {
    const {people = [], counts = {}, total = 0, filters = {}, expiring = {}} = this.props;

    // Derived rows are the same grant seen from a member's side — counted once, on the group.
    const grantCount = people.reduce(
        (n, p) => n + p.lines.filter((l) => !l.derived).length, 0);

    return (
      <div className="rounded-card roster" data-testid="ac-roster">
        <div className="roster-subtitle" data-testid="ac-roster-counts">{countsText(counts)}</div>

        <div className="roster-filters">
          <TextField
            select value={filters.source || ''} label="Source" variant="standard"
            onChange={(e) => this.props.onFilterChange({source: e.target.value})}
            inputProps={{'data-testid': 'ac-roster-source'}}
          >
            {SOURCES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
          <TextField
            select value={filters.scopeType || ''} label="Scope" variant="standard"
            onChange={(e) => this.props.onFilterChange({scopeType: e.target.value})}
            inputProps={{'data-testid': 'ac-roster-scope'}}
          >
            {SCOPE_TYPES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
          <TextField
            value={filters.q || ''} label="Search people, teams, services" variant="standard"
            onChange={(e) => this.props.onFilterChange({q: e.target.value})}
            inputProps={{'data-testid': 'ac-roster-search'}}
          />
          <span className="roster-actions">
            <Button label="Export" className="btn btn-default" onClick={this.handleExport}
              data-testid="ac-roster-export" />
            <Button label="+ Assign access" className="btn btn-action"
              onClick={this.props.onGrant} data-testid="ac-roster-assign" />
          </span>
        </div>

        {!people.length && (
          <div className="empty-hint" data-testid="ac-roster-empty">
            No subjects match these filters.
          </div>
        )}

        {!!people.length && (
          <Table size="small" className="data-table">
            <TableHead>
              <TableRow>
                <TableCell><strong>Subject</strong></TableCell>
                <TableCell><strong>Role / permission</strong></TableCell>
                <TableCell><strong>Scope</strong></TableCell>
                <TableCell><strong>Source</strong></TableCell>
                <TableCell><strong>Expires</strong></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {people.map((p) => {
                // A subject with no grants still appears — the roster is driven from `ac_subject`,
                // because "not on the list" reads as "has no access" and that is the opposite of
                // what an empty row means.
                const lines = p.lines.length ? p.lines : [null];
                return lines.map((l, i) => (
                  <TableRow
                    key={`${p.subjectId}-${l ? l.grantId : 'none'}`}
                    hover
                    data-testid={i === 0 ? 'ac-roster-row' : 'ac-roster-continuation'}
                  >
                    <TableCell className={i === 0 ? 'roster-subject' : 'roster-same'}>
                      {i === 0 ? (
                        <span>
                          <strong>{p.displayName}</strong>
                          <span className="subject-type">
                            {p.subjectType === 'group' ? ` Group · ${p.memberCount} members` :
                              p.subjectType === 'service' ? ' Service account' : ''}
                          </span>
                        </span>
                      ) : <span className="same-person">↳ same person</span>}
                    </TableCell>
                    <TableCell>{l ? (l.roleCode || l.permissionCode) : <em>no access</em>}</TableCell>
                    <TableCell>{l ? scopeText(l.scope) : ''}</TableCell>
                    <TableCell data-testid="ac-roster-source-cell">{l ? l.source.label : ''}</TableCell>
                    <TableCell>{l ? expiryText(l.expiresAt) : ''}</TableCell>
                    <TableCell>
                      {i === 0 && (
                        <Button label="Manage" className="btn btn-link"
                          onClick={() => this.props.onManage(p)} data-testid="ac-roster-manage" />
                      )}
                    </TableCell>
                  </TableRow>
                ));
              })}
            </TableBody>
          </Table>
        )}

        {/* The page cap, said out loud. A roster that silently truncates is worse than a search
            box, because it looks complete. */}
        <div className="roster-footer" data-testid="ac-roster-footer">
          {`Showing ${people.length} of ${total} subject${total === 1 ? '' : 's'} · ${grantCount} grant${grantCount === 1 ? '' : 's'} on this page`}
        </div>

        {!!expiring.expiring && (
          <Alert severity="warning" className="expiry-banner" data-testid="ac-roster-expiry">
            {`${expiring.expiring} transitional grant${expiring.expiring === 1 ? '' : 's'} expire within 14 days.`}
            {!!expiring.legacySuper && (
              <span>
                {' '}
                <code>legacy_super</code> was created by the migration so nobody lost access at
                switch-on. It cannot be extended by its own holder — replace it with real roles
                before it lapses.
              </span>
            )}
          </Alert>
        )}
      </div>
    );
  }
}

export default RosterTable;
